import React, { useState } from 'react';
import { ITrack } from '../../../../data/models/track';
import { arrayMove } from '@dnd-kit/sortable';
import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import './ReorderTracks.scss';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Spinner, { SpinnerSize } from '../../../common/Spinner/Spinner';
import { cloneDeep } from 'lodash';
import { DroppableVolumeTable } from './DroppableVolumeTable';
import CancelChangesModal from '../../../common/CancelChangesModal/cancelChangesModal';
import _ from 'lodash';

interface ReorderTracksProps {
  tracks: ITrack[];
  volumeNumbers: number[];
  updateProductTracks: (tracks: ITrack[]) => Promise<void>;
  closeComponent: () => void;
  formatVolumeHeader: (i: number) => string;
}

export interface IDroppableVolume {
  id: string;
  volumeNumber: number;
  tracks: IDraggableTrack[];
}

export interface IDraggableTrack {
  id: string;
  track: ITrack;
}

export default function ReorderTracks({
  tracks,
  volumeNumbers,
  updateProductTracks,
  closeComponent,
  formatVolumeHeader,
}: ReorderTracksProps) {
  const tracksCopy = cloneDeep(tracks);
  const [activeTrack, setActiveTrack] = useState<any>();
  const [showSpinner, setShowSpinner] = useState(false);
  const [volumes, setVolumes] = useState<IDroppableVolume[]>(
    volumeNumbers.map((v) => {
      return {
        id: `volume_${v}`,
        volumeNumber: v,
        tracks: tracksCopy
          .filter((t) => t.discOrSideNumber === v)
          .map((t) => {
            return {
              id: t.id!.toString(),
              track: t,
            };
          })
          .sort((x, y) => x.track.trackNumber - y.track.trackNumber),
      };
    })
  );
  const [showCancelChangesModal, setShowCancelChangesModal] = useState(false);
  const { t } = useTranslation();

  const sensors = useSensors(useSensor(PointerSensor));

  const findVolumeIndex = (id: any) => {
    return volumes.findIndex(
      (v) => v.id === id || v.tracks.some((t) => t.id.toString() === id)
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    const track = volumes
      .find((v) => v.tracks.some((t) => t.id === active.id))
      ?.tracks.find((t) => t.id === active.id);
    setActiveTrack(track);
  };

  /* This occurs while the user is still dragging a track and handles when a track is dragged between volumes. When this happens dnd-kit requiers us to remove the track from it's original volume array and insert it into the new volume array */
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    const oldVolumeIndex = findVolumeIndex(active?.id);
    const newVolumeIndex = findVolumeIndex(over?.id);

    if (
      oldVolumeIndex < 0 ||
      newVolumeIndex < 0 ||
      oldVolumeIndex === newVolumeIndex
    ) {
      return;
    }

    const oldVolume = volumes[oldVolumeIndex];
    const oldTrackIndex = oldVolume!.tracks.findIndex(
      (i) => i.id === active.id
    );

    const newVolume = volumes[newVolumeIndex];
    const isVolumeId = over?.id === newVolume.id;
    const newTrackIndex = isVolumeId
      ? 0
      : newVolume!.tracks.findIndex((i) => i.id === over!.id);

    const updatedVolumes = volumes.map((v) => {
      if (v.volumeNumber === oldVolume.volumeNumber) {
        return { ...v, tracks: v.tracks.toSpliced(oldTrackIndex, 1) };
      } else if (v.volumeNumber === newVolume.volumeNumber) {
        return {
          ...v,
          tracks: v.tracks.toSpliced(
            newTrackIndex,
            0,
            oldVolume.tracks[oldTrackIndex]
          ),
        };
      } else {
        return v;
      }
    });

    setVolumes(updatedVolumes);
  };

  /* This runs once the user drops the track and handles moving the track to it's new position and then updates the track numbers in that volume. If the track is originally from a different volume then those track numbers also get updated. */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const oldVolumeIndex = findVolumeIndex(active?.id);
    const newVolumeIndex = findVolumeIndex(over?.id);
    if (
      oldVolumeIndex < 0 ||
      newVolumeIndex < 0 ||
      oldVolumeIndex !== newVolumeIndex
    ) {
      return;
    }
    const oldVolume = volumes[oldVolumeIndex];
    const oldTrackIndex = oldVolume.tracks.findIndex((t) => t.id === active.id);
    const isVolumeId = over?.id === oldVolume.id;
    const newTrackIndex = isVolumeId
      ? 0
      : oldVolume.tracks.findIndex((t) => t.id === over!.id);
    const originalVolumeNumber =
      oldVolume.tracks[oldTrackIndex].track.discOrSideNumber;

    const updatedVolumes = volumes.map((volume) => {
      if (volume.volumeNumber === oldVolume.volumeNumber) {
        const newArray = arrayMove(volume.tracks, oldTrackIndex, newTrackIndex);
        if (originalVolumeNumber !== volume.volumeNumber) {
          newArray[newTrackIndex].track.discOrSideNumber = volume.volumeNumber;
        }
        return {
          ...volume,
          tracks: newArray.map((t, index) => {
            return { ...t, track: { ...t.track, trackNumber: index + 1 } };
          }),
        };
      }
      if (
        volume.volumeNumber === originalVolumeNumber &&
        originalVolumeNumber !== oldVolume.volumeNumber
      ) {
        return {
          ...volume,
          tracks: volume.tracks.map((t, index) => {
            return { ...t, track: { ...t.track, trackNumber: index + 1 } };
          }),
        };
      } else {
        return volume;
      }
    });

    setVolumes(updatedVolumes);
    setActiveTrack(null);
  };

  const getTracksArray = () => {
    return volumes
      .map((v) => v.tracks)
      .flat()
      .map((t) => t.track);
  };

  const saveTrackOrder = async () => {
    const allTracks = getTracksArray();
    setShowSpinner(true);
    await updateProductTracks(allTracks);
    setShowSpinner(false);
    closeComponent();
  };

  const onCancel = () => {
    const allTracks = getTracksArray();
    if (!_.isEqual(tracks, allTracks)) {
      setShowCancelChangesModal(true);
    } else {
      closeComponent();
    }
  };

  return (
    <>
      <CancelChangesModal
        onSubmit={saveTrackOrder}
        onClose={closeComponent}
        onCancel={() => setShowCancelChangesModal(false)}
        show={showCancelChangesModal}
        titleText={t('common.button.reorderTracks')}
        bodyText={t('track.cancelOrderChangesBody')}
      />
      <div className="reorder-tracks">
        {showSpinner ? (
          <Spinner cover={true} size={SpinnerSize.FullComponent} />
        ) : null}
        <div className="track-reorder-buttons">
          <button
            type="button"
            className={`btn btn-primary`}
            onClick={saveTrackOrder}
          >
            {t('common.button.saveTrackOrder')}
          </button>
          <button
            type="button"
            className={`btn btn-primary alt`}
            onClick={onCancel}
          >
            {t('common.button.cancel')}
          </button>
        </div>
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          {volumes.map((volume) => {
            return (
              <div className="track-container" key={volume.id}>
                <DroppableVolumeTable
                  volume={volume}
                  formatHeader={formatVolumeHeader}
                  activeTrack={activeTrack}
                ></DroppableVolumeTable>
              </div>
            );
          })}
          <DragOverlay className="drag-overlay">
            <div className="active-track">
              <span>{activeTrack?.track.trackNumber}</span>
              <span>{activeTrack?.track.title}</span>
            </div>
          </DragOverlay>
        </DndContext>
      </div>
    </>
  );
}
